import { useState, useEffect, useCallback } from 'react'
import { createPortal } from 'react-dom'
import {
  Send, Plus, Search, X, Edit2, Trash2, Save,
  MessageSquare, Download,
} from 'lucide-react'
import { submittalApi } from '../services/submittalApi'
import { exportToCsv } from '../services/csvExport'
import { projectApi } from '../services/projectApi'
import { LoadingSpinner } from '../components/common/LoadingSpinner'
import { EmptyState } from '../components/common/EmptyState'
import { StatusBadge } from '../components/common/StatusBadge'
import { ConfirmDialog } from '../components/common/ConfirmDialog'
import type {
  Submittal, SubmittalCreate, SubmittalUpdate,
  SubmittalStatus, SubmittalType, SubmittalReview, SubmittalReviewCreate,
  Project,
} from '../types'

const SUBMITTAL_TYPES: { value: SubmittalType; label: string }[] = [
  { value: 'TAB_Report', label: 'TAB Report' },
  { value: 'Controls_Checkout', label: 'Controls Checkout' },
  { value: 'Design_Review', label: 'Design Review' },
  { value: 'Shop_Drawing', label: 'Shop Drawing' },
  { value: 'O_M_Manual', label: 'O&M Manual' },
  { value: 'Training_Doc', label: 'Training Doc' },
  { value: 'Other', label: 'Other' },
]

const REVIEW_STATUSES: { value: SubmittalReviewCreate['status']; label: string }[] = [
  { value: 'Approved', label: 'Approved' },
  { value: 'Approved_As_Noted', label: 'Approved as Noted' },
  { value: 'Rejected', label: 'Rejected' },
  { value: 'Resubmit', label: 'Resubmit' },
]

function formatType(type: SubmittalType): string {
  return SUBMITTAL_TYPES.find(t => t.value === type)?.label || type.replace(/_/g, ' ')
}

export function Submittals() {
  const [items, setItems] = useState<Submittal[]>([])
  const [projects, setProjects] = useState<Project[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const [filterProject, setFilterProject] = useState('')
  const [filterStatus, setFilterStatus] = useState('')
  const [filterType, setFilterType] = useState('')
  const [searchTerm, setSearchTerm] = useState('')

  const [showModal, setShowModal] = useState(false)
  const [editingItem, setEditingItem] = useState<Submittal | null>(null)
  const [formData, setFormData] = useState({
    projectId: '' as string | number,
    submittalNumber: '',
    title: '',
    description: '',
    type: 'Other' as SubmittalType,
    status: 'Pending' as SubmittalStatus,
    submittedBy: '',
    dueDate: '',
  })
  const [saving, setSaving] = useState(false)
  const [deleteId, setDeleteId] = useState<string | null>(null)

  // Review panel state
  const [reviewSubmittal, setReviewSubmittal] = useState<(Submittal & { reviews: SubmittalReview[] }) | null>(null)
  const [showReviewPanel, setShowReviewPanel] = useState(false)
  const [reviewForm, setReviewForm] = useState<SubmittalReviewCreate>({ status: 'Approved', comments: '' })
  const [submittingReview, setSubmittingReview] = useState(false)

  const loadItems = useCallback(async () => {
    try {
      setLoading(true)
      setError(null)
      const data = await submittalApi.getAll({
        projectId: filterProject || undefined,
        status: filterStatus || undefined,
        type: filterType || undefined,
      })
      setItems(data)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load submittals')
    } finally {
      setLoading(false)
    }
  }, [filterProject, filterStatus, filterType])

  useEffect(() => { loadItems() }, [loadItems])

  useEffect(() => {
    projectApi.getAll({}).then((result) => setProjects(result.items)).catch(() => {})
  }, [])

  function openCreate() {
    setEditingItem(null)
    setFormData({ projectId: filterProject || '', submittalNumber: '', title: '', description: '', type: 'Other', status: 'Pending', submittedBy: '', dueDate: '' })
    setShowModal(true)
  }

  function openEdit(item: Submittal) {
    setEditingItem(item)
    setFormData({
      projectId: item.projectId,
      submittalNumber: item.submittalNumber,
      title: item.title,
      description: item.description || '',
      type: item.type,
      status: item.status,
      submittedBy: item.submittedBy || '',
      dueDate: item.dueDate || '',
    })
    setShowModal(true)
  }

  async function openReview(item: Submittal) {
    try {
      const detail = await submittalApi.getById(String(item.id))
      setReviewSubmittal(detail)
      setReviewForm({ status: 'Approved', comments: '' })
      setShowReviewPanel(true)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load submittal')
    }
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    try {
      setSaving(true)
      if (editingItem) {
        await submittalApi.update(String(editingItem.id), {
          title: formData.title,
          description: formData.description,
          type: formData.type,
          status: formData.status,
          submittedBy: formData.submittedBy || undefined,
          dueDate: formData.dueDate || undefined,
        } as SubmittalUpdate)
      } else {
        await submittalApi.create({
          projectId: Number(formData.projectId),
          submittalNumber: formData.submittalNumber,
          title: formData.title,
          description: formData.description,
          type: formData.type,
          submittedBy: formData.submittedBy || undefined,
          dueDate: formData.dueDate || undefined,
        } as SubmittalCreate)
      }
      setShowModal(false)
      loadItems()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save submittal')
    } finally {
      setSaving(false)
    }
  }

  async function handleAddReview() {
    if (!reviewSubmittal) return
    try {
      setSubmittingReview(true)
      await submittalApi.addReview(String(reviewSubmittal.id), reviewForm)
      // Reload detail
      const detail = await submittalApi.getById(String(reviewSubmittal.id))
      setReviewSubmittal(detail)
      setReviewForm({ status: 'Approved', comments: '' })
      loadItems()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to add review')
    } finally {
      setSubmittingReview(false)
    }
  }

  async function handleDelete() {
    if (!deleteId) return
    try {
      await submittalApi.delete(deleteId)
      setDeleteId(null)
      loadItems()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete')
    }
  }

  const filteredItems = searchTerm
    ? items.filter((i) =>
        i.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        i.submittalNumber.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (i.description || '').toLowerCase().includes(searchTerm.toLowerCase())
      )
    : items

  function projectName(projectId: number): string {
    const p = projects.find(proj => String(proj.id) === String(projectId))
    return p ? (p as unknown as { projectName?: string }).projectName || (p as unknown as { name?: string }).name || `#${projectId}` : `#${projectId}`
  }

  return (
    <div className="page-container animate-fade-in">
      <div className="page-header">
        <div>
          <h1>Submittals</h1>
          <p className="page-subtitle">Track contractor submittals and CxA review cycles</p>
        </div>
        <div className="page-header-actions">
          {filteredItems.length > 0 && (
            <button className="btn btn-secondary" onClick={() => {
              exportToCsv(`submittals_${new Date().toISOString().split('T')[0]}.csv`,
                ['Number', 'Title', 'Project', 'Type', 'Status', 'Submitted By', 'Due Date', 'Reviews', 'Description'],
                filteredItems.map(i => [i.submittalNumber, i.title, projectName(i.projectId), formatType(i.type), i.status.replace(/_/g, ' '), i.submittedBy || '', i.dueDate || '', String(i.reviewCount ?? i.currentReviewNumber), i.description || ''])
              )
            }}>
              <Download size={16} /> Export CSV
            </button>
          )}
          <button className="btn btn-primary" onClick={openCreate}>
            <Plus size={16} /> New Submittal
          </button>
        </div>
      </div>

      {error && (
        <div className="info-card danger mb-4">
          <p style={{ margin: 0, color: 'var(--accent-red)' }}>{error}</p>
        </div>
      )}

      <div className="filters-bar">
        <div className="input-group" style={{ flex: '1', maxWidth: '300px' }}>
          <Search size={16} className="input-icon" />
          <input className="form-input search-input" placeholder="Search submittals..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} />
        </div>
        <select className="form-select" value={filterProject} onChange={(e) => setFilterProject(e.target.value)}>
          <option value="">All Projects</option>
          {projects.map((p) => <option key={p.id} value={p.id}>{(p as unknown as { projectName?: string }).projectName || (p as unknown as { name?: string }).name}</option>)}
        </select>
        <select className="form-select" value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)}>
          <option value="">All Statuses</option>
          <option value="Pending">Pending</option>
          <option value="Under_Review">Under Review</option>
          <option value="Approved">Approved</option>
          <option value="Approved_As_Noted">Approved as Noted</option>
          <option value="Rejected">Rejected</option>
          <option value="Resubmit">Resubmit</option>
        </select>
        <select className="form-select" value={filterType} onChange={(e) => setFilterType(e.target.value)}>
          <option value="">All Types</option>
          {SUBMITTAL_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
        </select>
      </div>

      {loading ? (
        <LoadingSpinner message="Loading submittals..." />
      ) : filteredItems.length === 0 ? (
        <EmptyState
          icon={<Send size={48} />}
          title="No submittals found"
          message={searchTerm || filterProject || filterStatus || filterType ? 'Try adjusting your filters.' : 'No submittals recorded yet.'}
          action={!searchTerm && !filterProject ? <button className="btn btn-primary" onClick={openCreate}><Plus size={16} /> Add Submittal</button> : undefined}
        />
      ) : (
        <div className="table-container">
          <div className="table-summary">{filteredItems.length} submittal{filteredItems.length !== 1 ? 's' : ''}</div>
          <table className="data-table">
            <thead>
              <tr>
                <th>Number</th>
                <th>Title</th>
                <th>Project</th>
                <th>Type</th>
                <th>Status</th>
                <th>Due Date</th>
                <th>Reviews</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredItems.map((item) => (
                <tr key={item.id}>
                  <td className="font-medium" style={{ whiteSpace: 'nowrap' }}>{item.submittalNumber}</td>
                  <td className="font-medium" style={{ cursor: 'pointer' }} onClick={() => openReview(item)}>{item.title}</td>
                  <td className="text-secondary-color">{projectName(item.projectId)}</td>
                  <td><span className="badge badge-gray-light">{formatType(item.type)}</span></td>
                  <td><StatusBadge status={item.status} /></td>
                  <td className="text-secondary-color">{item.dueDate ? new Date(item.dueDate).toLocaleDateString() : '-'}</td>
                  <td className="text-secondary-color">{item.reviewCount ?? item.currentReviewNumber}</td>
                  <td>
                    <div className="table-actions">
                      <button className="btn btn-ghost btn-xs btn-icon" title="Review" onClick={() => openReview(item)}><MessageSquare size={14} /></button>
                      <button className="btn btn-ghost btn-xs btn-icon" title="Edit" onClick={() => openEdit(item)}><Edit2 size={14} /></button>
                      <button className="btn btn-ghost btn-xs btn-icon text-danger" title="Delete" onClick={() => setDeleteId(String(item.id))}><Trash2 size={14} /></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Create/Edit Modal */}
      {showModal && createPortal(
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal modal-lg modal-scrollable" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{editingItem ? 'Edit Submittal' : 'New Submittal'}</h2>
              <button className="modal-close" onClick={() => setShowModal(false)}><X size={20} /></button>
            </div>
            <form onSubmit={handleSubmit}>
              <div className="modal-body">
                {!editingItem && (
                  <div className="form-group">
                    <label className="required">Project</label>
                    <select className="form-select" required value={formData.projectId} onChange={(e) => setFormData({ ...formData, projectId: e.target.value })}>
                      <option value="">Select project...</option>
                      {projects.map((p) => <option key={p.id} value={p.id}>{(p as unknown as { projectName?: string }).projectName || (p as unknown as { name?: string }).name}</option>)}
                    </select>
                  </div>
                )}
                <div className="form-row">
                  <div className="form-group">
                    <label className="required">Submittal Number</label>
                    <input className="form-input" required value={formData.submittalNumber} onChange={(e) => setFormData({ ...formData, submittalNumber: e.target.value })} placeholder="e.g., S-001" readOnly={!!editingItem} />
                  </div>
                  <div className="form-group">
                    <label className="required">Type</label>
                    <select className="form-select" required value={formData.type} onChange={(e) => setFormData({ ...formData, type: e.target.value as SubmittalType })}>
                      {SUBMITTAL_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                    </select>
                  </div>
                </div>
                <div className="form-group">
                  <label className="required">Title</label>
                  <input className="form-input" required value={formData.title} onChange={(e) => setFormData({ ...formData, title: e.target.value })} />
                </div>
                {editingItem && (
                  <div className="form-group">
                    <label>Status</label>
                    <select className="form-select" value={formData.status} onChange={(e) => setFormData({ ...formData, status: e.target.value as SubmittalStatus })}>
                      <option value="Pending">Pending</option>
                      <option value="Under_Review">Under Review</option>
                      <option value="Approved">Approved</option>
                      <option value="Approved_As_Noted">Approved as Noted</option>
                      <option value="Rejected">Rejected</option>
                      <option value="Resubmit">Resubmit</option>
                    </select>
                  </div>
                )}
                <div className="form-row">
                  <div className="form-group">
                    <label>Submitted By</label>
                    <input className="form-input" value={formData.submittedBy} onChange={(e) => setFormData({ ...formData, submittedBy: e.target.value })} placeholder="Contractor name" />
                  </div>
                  <div className="form-group">
                    <label>Due Date</label>
                    <input type="date" className="form-input" value={formData.dueDate} onChange={(e) => setFormData({ ...formData, dueDate: e.target.value })} />
                  </div>
                </div>
                <div className="form-group">
                  <label>Description</label>
                  <textarea className="form-textarea" value={formData.description} onChange={(e) => setFormData({ ...formData, description: e.target.value })} rows={3} />
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => setShowModal(false)}>Cancel</button>
                <button type="submit" className="btn btn-primary" disabled={saving}>
                  <Save size={16} /> {saving ? 'Saving...' : editingItem ? 'Update' : 'Create'}
                </button>
              </div>
            </form>
          </div>
        </div>, document.body
      )}

      {/* Review Panel Modal */}
      {showReviewPanel && reviewSubmittal && createPortal(
        <div className="modal-overlay" onClick={() => setShowReviewPanel(false)}>
          <div className="modal modal-lg modal-scrollable" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <div>
                <h2>{reviewSubmittal.submittalNumber} — {reviewSubmittal.title}</h2>
                <div className="d-flex gap-2 mt-1">
                  <span className="badge badge-gray-light">{formatType(reviewSubmittal.type)}</span>
                  <StatusBadge status={reviewSubmittal.status} />
                </div>
              </div>
              <button className="modal-close" onClick={() => setShowReviewPanel(false)}><X size={20} /></button>
            </div>
            <div className="modal-body">
              {reviewSubmittal.description && (
                <p className="text-secondary-color mb-4">{reviewSubmittal.description}</p>
              )}
              <div className="d-flex gap-4 mb-4 text-sm text-secondary-color">
                {reviewSubmittal.submittedBy && <span>Submitted by: <strong>{reviewSubmittal.submittedBy}</strong></span>}
                {reviewSubmittal.dueDate && <span>Due: <strong>{new Date(reviewSubmittal.dueDate).toLocaleDateString()}</strong></span>}
                <span>Created: <strong>{new Date(reviewSubmittal.createdAt).toLocaleDateString()}</strong></span>
              </div>

              {/* Review History */}
              <h3 style={{ marginBottom: '12px' }}>Review History</h3>
              {reviewSubmittal.reviews.length === 0 ? (
                <p className="text-secondary-color text-sm" style={{ marginBottom: '16px' }}>No reviews yet.</p>
              ) : (
                <div style={{ marginBottom: '16px' }}>
                  {reviewSubmittal.reviews.map((review) => (
                    <div key={review.id} className="info-card" style={{ marginBottom: '8px', padding: '12px 16px' }}>
                      <div className="d-flex justify-between align-center mb-1">
                        <div className="d-flex gap-2 align-center">
                          <strong>Review #{review.reviewNumber}</strong>
                          <StatusBadge status={review.status} />
                        </div>
                        <span className="text-sm text-secondary-color">
                          {review.reviewDate ? new Date(review.reviewDate).toLocaleDateString() : ''}
                        </span>
                      </div>
                      {review.reviewedByName && (
                        <div className="text-sm text-secondary-color">by {review.reviewedByName}</div>
                      )}
                      {review.comments && (
                        <p className="text-sm" style={{ marginTop: '8px', marginBottom: 0 }}>{review.comments}</p>
                      )}
                    </div>
                  ))}
                </div>
              )}

              {/* Add Review Form */}
              <h3 style={{ marginBottom: '12px' }}>Add Review</h3>
              <div className="info-card" style={{ padding: '16px' }}>
                <div className="form-group">
                  <label className="required">Decision</label>
                  <select className="form-select" value={reviewForm.status} onChange={(e) => setReviewForm({ ...reviewForm, status: e.target.value as SubmittalReviewCreate['status'] })}>
                    {REVIEW_STATUSES.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
                  </select>
                </div>
                <div className="form-group">
                  <label>Comments</label>
                  <textarea className="form-textarea" value={reviewForm.comments || ''} onChange={(e) => setReviewForm({ ...reviewForm, comments: e.target.value })} rows={3} placeholder="Review comments..." />
                </div>
                <button
                  type="button"
                  className="btn btn-primary"
                  disabled={submittingReview}
                  onClick={handleAddReview}
                >
                  <Save size={16} /> {submittingReview ? 'Submitting...' : 'Submit Review'}
                </button>
              </div>
            </div>
            <div className="modal-footer">
              <button type="button" className="btn btn-secondary" onClick={() => setShowReviewPanel(false)}>Close</button>
            </div>
          </div>
        </div>, document.body
      )}

      <ConfirmDialog
        isOpen={!!deleteId}
        title="Delete Submittal"
        message="Are you sure you want to delete this submittal and all its reviews?"
        confirmLabel="Delete"
        variant="danger"
        onConfirm={handleDelete}
        onCancel={() => setDeleteId(null)}
      />
    </div>
  )
}
